import type { BlockMentionValues, ClauseListKey, ClauseTypes, GroupedClauses, ResourceBlock } from '@root/@types/types';
import { filter, flow, groupBy, map, partialRight, reduce } from 'lodash-es';
import pluralize from 'pluralize';

import { getAllBlocks, isClauseBlock } from '../blocks';

import { CLAUSE_TYPES } from './constants';

export const keyMap: Record<ClauseTypes, ClauseListKey> = {
  Condition: 'list_of_condition',
  Endorsement: 'list_of_endorsement',
  Exclusion: 'list_of_exclusion',
  Wording: 'list_of_wording',
  'Express Warranties': 'list_of_express_warranties',
  'Standard Warranties': 'list_of_standard_warranties',
};

const CLAUSE_NAME_FALLBACK = 'Untitled clause';

export const getClauseListEntryContent = ({ clause_reference, name }: ResourceBlock) => {
  return clause_reference ? `${clause_reference} - ${name || CLAUSE_NAME_FALLBACK}` : name || CLAUSE_NAME_FALLBACK;
};

export const getBlockDeletedCopy = (name: string) => `<p>${name}&nbsp;has hereby been removed</p>`;

export const getClauseTypeForClauseListKey = (value: ClauseListKey): ClauseTypes | undefined => {
  const keys = Object.keys(keyMap) as ClauseTypes[];
  return keys.find((key) => keyMap[key] === value);
};

export const getClauseList = (
  blocks: ResourceBlock[] = [],
  type: ClauseTypes,
  fallback = `No ${pluralize(type.toLocaleLowerCase())} specified`,
): string => {
  if (blocks.length === 0) return fallback;

  return blocks
    .map((block) => `<span class="mention-list-item">${getClauseListEntryContent(block)}, as attached.</span>`)
    .join('');
};

export const getClausesAsMentions = (clauses: GroupedClauses): BlockMentionValues => {
  const lists: Record<ClauseListKey, string> = flow(
    partialRight(map, (type: ClauseTypes) => ({
      [keyMap[type]]: getClauseList(clauses[type], type),
    })),
    partialRight(reduce, (acc: any, next: any) => ({ ...acc, ...next }), {}),
  )(CLAUSE_TYPES);

  return lists;
};

type EntityWithSections = {
  sections: Array<{
    id: string;
    layout?: string[];
    blocks: { id: string }[];
  }>;
};

export const getGroupedClauses = (entity?: EntityWithSections): GroupedClauses => {
  if (!entity?.sections) {
    return {} as GroupedClauses;
  }

  return flow(
    partialRight(getAllBlocks),
    partialRight(
      filter,
      (block: Pick<ResourceBlock, 'deleted_at' | 'type'>) => !block.deleted_at && isClauseBlock(block),
    ),
    partialRight(groupBy, 'clause_type'),
  )(entity);
};
